import { useState } from 'react';
import { Icon, showToast, EmptyState } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  IntegrationCard,
  WebhookCard,
  LoadingState,
  ErrorState,
  OrganizationCard,
  ClioConfigModal,
  QBOConfigModal,
  EmailConfigModal,
  TenantSettingsModal,
} from '../components';
import { DataReplicationCard } from '@xtnd/web/features/data-replication/components';
import {
  useTenantConfig,
  useConfigurationStatus,
  useClioConfigMutation,
  useQboConfigMutation,
  useTenantSettingsMutation,
  useEmailConfigMutation,
  useModuleHealthCheck,
} from '../queries';
import type {
  ClioModuleConfig,
  ModuleConfig,
  EmailModuleConfig,
} from '@xtnd/web/shared/types/tenantSettingTypes';

export default function SettingsPage() {

  // Modal states
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [moduleHealthData, setModuleHealthData] = useState<Record<string, any>>({});

  // Fetch tenant configuration
  const {
    data: tenantConfig,
    isLoading: isLoadingConfig,
    error,
    isError,
    refetch,
  } = useTenantConfig();

  // Fetch configuration status (only after tenant config is loaded)
  const {
    data: configurationStatus,
    isLoading: isLoadingStatus,
  } = useConfigurationStatus(!!tenantConfig);

  // Mutations
  const clioConfigMutation = useClioConfigMutation();
  const qboConfigMutation = useQboConfigMutation();
  const tenantSettingsMutation = useTenantSettingsMutation();
  const emailConfigMutation = useEmailConfigMutation();
  const moduleHealthCheckMutation = useModuleHealthCheck();

  // Combined loading state - wait for both APIs
  const isLoading = isLoadingConfig || (!!tenantConfig && isLoadingStatus);

  // Show error toast when tenant config fails
  if (isError && error instanceof Error) {
    showToast.error(`Failed to load settings: ${error.message}`);
  }

  const handleConfigureIntegration = (moduleName: string) => {
    setActiveModal(moduleName);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  const handleHealthCheck = async (moduleName: string) => {
    setModuleHealthData(prev => ({
      ...prev,
      [moduleName]: { status: 'checking', message: 'Checking module health...' },
    }));

    try {
      const result = await moduleHealthCheckMutation.mutateAsync(moduleName);
      setModuleHealthData(prev => ({
        ...prev,
        [moduleName]: result.health[moduleName],
      }));
    } catch {
      setModuleHealthData(prev => ({
        ...prev,
        [moduleName]: {
          status: 'error',
          message: 'Health check failed',
          lastChecked: new Date().toISOString(),
        },
      }));
    }
  };

  // Helper function to filter active integrations
  // Excludes template features and dataReplicationAdmin (shown in its own section)
  const getActiveIntegrations = () => {
    if (!tenantConfig?.features) return [];
    return tenantConfig.features.filter(
      f => f.enabled && 
           f.moduleName !== 'emailTemplates' && 
           f.moduleName !== 'pdfTemplates' &&
           f.moduleName !== 'dataReplicationAdmin'
    );
  };

  if (isError && error instanceof Error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  return (
    <PageLayout
      title="Settings"
      description="Manage integrations, webhooks, and system configuration"
      icon="HiCog"
      isLoading={isLoading}
      customSkeleton={<LoadingState />}
    >
      <div className="space-y-8">

      {/* Organization Info Card */}
      <OrganizationCard
        tenantName={tenantConfig?.tenantName}
        tenantId={tenantConfig?.tenantId}
        onEdit={() => setActiveModal('tenantSettings')}
      />

      {/* Main Content */}
      <div className="space-y-8">
        {/* Integrations Section */}
        <div>
          <div className="flex items-center space-x-3 mb-6">
            <Icon name="HiPuzzle" className="h-6 w-6 text-xtnd-blue" />
            <h2 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white">
              Active Integrations
            </h2>
          </div>

          {getActiveIntegrations().length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getActiveIntegrations().map(feature => {
                  const configStatus = configurationStatus?.modules.find(
                    module => module.module === feature.moduleName
                  );

                  return (
                    <IntegrationCard
                      key={feature.moduleName}
                      feature={feature}
                      moduleConfig={
                        tenantConfig?.modules[feature.moduleName] || ({} as ModuleConfig)
                      }
                      onConfigure={handleConfigureIntegration}
                      onHealthCheck={handleHealthCheck}
                      health={moduleHealthData[feature.moduleName]}
                      configurationStatus={configStatus}
                    />
                  );
                })}
            </div>
          ) : (
            <EmptyState
              icon="HiPuzzle"
              title="No Integrations Enabled"
              description="Contact your administrator to enable integrations"
              variant="default"
              padding="md"
              titleSize="sm"
            />
          )}
        </div>

        {/* Webhooks Section */}
        {(tenantConfig?.modules?.clioAPI || tenantConfig?.modules?.qboAPI) && (
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <Icon name="HiGlobe" className="h-6 w-6 text-xtnd-blue" />
              <h2 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white">
                Webhook Management
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Clio Webhooks */}
              {tenantConfig?.modules?.clioAPI && (
                <WebhookCard
                  title="Clio Webhooks"
                  description="Real-time updates for bills, matters, and contacts from Clio"
                  iconColor="blue"
                  isActive={!!tenantConfig.modules.clioAPI.webhooksConfigured}
                  manageRoute="/settings/webhooks"
                  activityRoute="/settings/webhook-activity"
                  features={[
                    { icon: 'HiDocumentText', label: 'Lifecycle Manager' },
                    { icon: 'HiChartBar', label: 'Activity Monitor' },
                  ]}
                />
              )}

              {/* QBO Webhooks */}
              {tenantConfig?.modules?.qboAPI && (
                <WebhookCard
                  title="QuickBooks Online"
                  description="Auto-process invoices, customers, and payments from QBO"
                  iconColor="green"
                  isActive={!!tenantConfig.modules.qboAPI.configured}
                  manageRoute="/settings/qbo-webhooks"
                  activityRoute="/settings/qbo-webhook-activity"
                  features={[
                    { icon: 'HiLightningBolt', label: 'Auto-Processing' },
                    { icon: 'HiCollection', label: 'Queue Management' },
                  ]}
                />
              )}
            </div>
          </div>
        )}

        {/* Data Replication Section - Only show if dataReplicationAdmin feature is enabled */}
        {tenantConfig?.modules?.clioAPI && 
         tenantConfig?.features?.some(f => f.moduleName === 'dataReplicationAdmin' && f.enabled) && (
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <Icon name="HiDatabase" className="h-6 w-6 text-xtnd-blue" />
              <h2 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white">
                Data Replication
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <DataReplicationCard
                title="Replication Configuration"
                description="Configure Clio to DynamoDB toggles, DW and Redshift entity enablement, and fiscal or calendar report extraction settings"
                iconColor="indigo"
                isActive={true}
                manageRoute="/settings/data-replication/configuration"
                features={[
                  { icon: 'HiAdjustments', label: 'Toggle Management' },
                  { icon: 'HiChartBar', label: 'DW Settings' },
                ]}
              />

              {/* Clio Matters */}
              <DataReplicationCard
                title="Clio Matters"
                description="Manage replicated matters data from Clio for offline access and performance"
                iconColor="purple"
                isActive={true}
                manageRoute="/settings/data-replication/matters"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Clio Contacts */}
              <DataReplicationCard
                title="Clio Contacts"
                description="Manage replicated contacts data from Clio for offline access and performance"
                iconColor="blue"
                isActive={true}
                manageRoute="/settings/data-replication/contacts"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Clio Bills */}
              <DataReplicationCard
                title="Clio Bills"
                description="Manage replicated bills data from Clio for offline access and performance"
                iconColor="green"
                isActive={true}
                manageRoute="/settings/data-replication/bills"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Clio Bank Accounts */}
              <DataReplicationCard
                title="Clio Bank Accounts"
                description="Manage replicated bank accounts data from Clio for offline access and performance"
                iconColor="blue"
                isActive={true}
                manageRoute="/settings/data-replication/bank-accounts"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Clio Bank Transactions */}
              <DataReplicationCard
                title="Clio Bank Transactions"
                description="Manage replicated bank transactions data from Clio for offline access and performance"
                iconColor="indigo"
                isActive={true}
                manageRoute="/settings/data-replication/bank-transactions"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Clio Activities */}
              <DataReplicationCard
                title="Clio Activities"
                description="Manage replicated activities (time entries & expenses) from Clio for offline access and performance"
                iconColor="amber"
                isActive={true}
                manageRoute="/settings/data-replication/activities"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Activity Descriptions */}
              <DataReplicationCard
                title="Activity Descriptions"
                description="Manage replicated activity descriptions (time entry categories) from Clio for offline access and performance"
                iconColor="pink"
                isActive={true}
                manageRoute="/settings/data-replication/activity-descriptions"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Expense Categories */}
              <DataReplicationCard
                title="Expense Categories"
                description="Manage replicated expense categories from Clio for offline access and performance"
                iconColor="emerald"
                isActive={true}
                manageRoute="/settings/data-replication/expense-categories"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Tasks */}
              <DataReplicationCard
                title="Tasks"
                description="Manage replicated tasks (matter tasks) from Clio for offline access and performance"
                iconColor="orange"
                isActive={true}
                manageRoute="/settings/data-replication/tasks"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* UTBMS Codes */}
              <DataReplicationCard
                title="UTBMS Codes"
                description="Manage replicated UTBMS codes from Clio for offline access and performance"
                iconColor="red"
                isActive={true}
                manageRoute="/settings/data-replication/utbms-codes"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* UTBMS Sets */}
              <DataReplicationCard
                title="UTBMS Sets"
                description="Manage replicated UTBMS sets from Clio for offline access and performance"
                iconColor="teal"
                isActive={true}
                manageRoute="/settings/data-replication/utbms-sets"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Line Items */}
              <DataReplicationCard
                title="Line Items"
                description="Manage replicated line items data from Clio for offline access and performance"
                iconColor="cyan"
                isActive={true}
                manageRoute="/settings/data-replication/line-items"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Allocations */}
              <DataReplicationCard
                title="Allocations"
                description="Manage replicated allocations data from Clio for offline access and performance"
                iconColor="violet"
                isActive={true}
                manageRoute="/settings/data-replication/allocations"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />

              {/* Users */}
              <DataReplicationCard
                title="Users"
                description="Manage replicated users data from Clio for offline access and performance"
                iconColor="gray"
                isActive={true}
                manageRoute="/settings/data-replication/users"
                features={[
                  { icon: 'HiSearch', label: 'Search & Sync' },
                  { icon: 'HiRefresh', label: 'Force Replication' },
                ]}
              />
            </div>
          </div>
        )}
      </div>

      {/* Dynamic Configuration Modals */}
      <ClioConfigModal
        isOpen={activeModal === 'clioAPI'}
        onClose={handleCloseModal}
        onSave={async data => {
          await clioConfigMutation.mutateAsync(data);
          setActiveModal(null);
        }}
        initialData={tenantConfig?.modules?.clioAPI as ClioModuleConfig | undefined}
        isLoading={clioConfigMutation.isPending}
      />

      <QBOConfigModal
        isOpen={activeModal === 'qboAPI'}
        onClose={handleCloseModal}
        onSave={async data => {
          await qboConfigMutation.mutateAsync(data);
          setActiveModal(null);
        }}
        initialData={tenantConfig?.modules?.qboAPI}
        isLoading={qboConfigMutation.isPending}
      />

      <TenantSettingsModal
        isOpen={activeModal === 'tenantSettings'}
        onClose={handleCloseModal}
        onSave={async data => {
          await tenantSettingsMutation.mutateAsync(data);
          setActiveModal(null);
        }}
        initialData={
          {
            ...tenantConfig?.modules.tenantSettings,
            tenantName: tenantConfig?.tenantName,
            alias: tenantConfig?.alias,
            authorizedDomains: tenantConfig?.authorizedDomains,
            allowedEmails: tenantConfig?.allowedEmails,
            emailServiceConfig: tenantConfig?.modules?.emailServiceConfig,
            updatedAt: tenantConfig?.updatedAt,
          } as any
        }
        isLoading={tenantSettingsMutation.isPending}
      />

      <EmailConfigModal
        isOpen={activeModal === 'emailNotifications'}
        onClose={handleCloseModal}
        onSave={async data => {
          await emailConfigMutation.mutateAsync(data);
          setActiveModal(null);
        }}
        initialData={
          tenantConfig?.modules.tenantSettings?.emailNotifications as EmailModuleConfig | undefined
        }
        isLoading={emailConfigMutation.isPending}
      />
      </div>
    </PageLayout>
  );
}
